import React from 'react'
import { LoadingOutlined, WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import {
  getHealthPlanValidationSummary,
  type HealthPlanValidationSummaryDto,
} from '../api/health-plan-api'
import {
  ValidationBannerBody,
  ValidationBannerContainer,
  ValidationBannerCount,
  ValidationBannerHeader,
  ValidationBannerIcon,
  ValidationBannerList,
  ValidationBannerListItem,
  ValidationBannerTitle,
} from './HealthPlanValidationBanner.styles'

interface BulletDef {
  statKey: keyof HealthPlanValidationSummaryDto
  noun: string
  description: string
}

const BULLET_DEFS: readonly BulletDef[] = [
  {
    statKey: 'serviceAreaMismatchCount',
    noun: 'service areas',
    description: 'with mismatched data',
  },
  {
    statKey: 'missingRatingAreaPriceCount',
    noun: 'rating areas',
    description: 'missing price data',
  },
  {
    statKey: 'missingBenefitsCount',
    noun: 'plans',
    description: 'missing benefits',
  },
  {
    statKey: 'countyMismatchCount',
    noun: 'counties',
    description: 'have mismatched name or aliases',
  },
]

interface Props {
  year: number | undefined
}

export default function HealthPlanValidationBanner({ year }: Props) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['healthPlanValidationSummary', year],
    queryFn: () => {
      if (year === undefined)
        return Promise.reject(new Error('year is required'))
      return getHealthPlanValidationSummary(year)
    },
    enabled: year !== undefined,
  })

  // isLoading (not `isLoading || !data`) is specifically "the first fetch is
  // in flight" — it's false once the query errors, and false while disabled
  // (year still undefined), so neither case gets stuck showing "Checking...".
  if (isLoading) {
    return (
      <ValidationBannerContainer $variant="loading">
        <ValidationBannerHeader>
          <ValidationBannerIcon $variant="loading">
            <LoadingOutlined />
          </ValidationBannerIcon>
          <ValidationBannerTitle $variant="loading">
            Checking for validation issues...
          </ValidationBannerTitle>
        </ValidationBannerHeader>
      </ValidationBannerContainer>
    )
  }

  if (isError || !data) return null

  const bullets = BULLET_DEFS.filter((def) => data[def.statKey] > 0)

  if (bullets.length === 0) return null

  return (
    <ValidationBannerContainer $variant="warning">
      <ValidationBannerBody>
        <ValidationBannerHeader>
          <ValidationBannerIcon $variant="warning">
            <WarningOutlined />
          </ValidationBannerIcon>
          <ValidationBannerTitle $variant="warning">
            Validation issues found
          </ValidationBannerTitle>
        </ValidationBannerHeader>
        <ValidationBannerList>
          {bullets.map((def) => (
            <ValidationBannerListItem key={def.statKey}>
              <ValidationBannerCount>
                {data[def.statKey].toLocaleString()} {def.noun}
              </ValidationBannerCount>{' '}
              {def.description}
            </ValidationBannerListItem>
          ))}
        </ValidationBannerList>
      </ValidationBannerBody>
    </ValidationBannerContainer>
  )
}
